export interface SignedRegistrationResult {
  playerSigned: string;
  registered: boolean;
}

interface AwaitRegisteredOptions {
  attempts?: number;
  delayMs?: number;
  sleep?: (ms: number) => Promise<void>;
}

type SignedPlayerSource = Pick<JestSDKApi, "getPlayerSigned">;

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// showRegistrationOverlay() can resolve before the SDK reflects the new
// registered state, so the first getPlayerSigned() after it may still carry
// registered=false. Minting a session from that stale token sends
// registered:false to the backend and the account stays flagged jestGuest.
//
// Re-read getPlayerSigned() until the signed payload reports registered=true,
// up to `attempts` tries spaced `delayMs` apart. Returns as soon as it sees a
// registered token; if the budget is exhausted it returns the last (still
// guest) read so the caller can detect the non-transition and avoid a false
// "account created" success.
export async function awaitRegisteredSignedPlayer(
  sdk: SignedPlayerSource,
  opts: AwaitRegisteredOptions = {},
): Promise<SignedRegistrationResult> {
  const attempts = opts.attempts ?? 6;
  const delayMs = opts.delayMs ?? 500;
  const sleep = opts.sleep ?? defaultSleep;

  let last: SignedRegistrationResult = { playerSigned: "", registered: false };

  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      const res = await sdk.getPlayerSigned();
      last = {
        playerSigned: res.playerSigned,
        registered: res.player.registered,
      };
      if (last.registered) return last;
    } catch {
      // a single read can fail transiently (host round-trip); keep polling
      // within the budget rather than aborting the whole confirmation.
    }
    if (attempt < attempts - 1) await sleep(delayMs);
  }

  return last;
}
